import { useState, useEffect } from 'react';
import { Check, X, Building2, User, Mail, Phone, FileText, Calendar, AlertTriangle, CheckCircle, Clock, XCircle, Eye, Briefcase, AlertCircle, Copy } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { toast } from 'sonner';
import { fetchWithAuth } from '../utils/api';
import { ConfirmDialog } from './ConfirmDialog';
import { useAdminModalZIndex } from './AdminModalZContext';
import { devError } from '../utils/logger';


// Helper function for date formatting
const formatDistanceToNow = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 1) return 'akkurat nå';
  if (diffInMinutes < 60) return `${diffInMinutes} min siden`;
  if (diffInHours < 24) return `${diffInHours} t siden`;
  if (diffInDays < 7) return `${diffInDays} d siden`;
  return date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' });
};

interface EmployerProfile {
  id: string;
  name: string;
  email: string;
  organizationNumber: string;
  organizationNameVerified?: string;
  organizationRole: string;
  organizationRoleOther?: string;
  verificationStatus: 'pending' | 'approved' | 'rejected';
  verificationNotes?: string;
  createdAt: string;
  verifiedAt?: string;
  bronnoyundValidationFailed?: boolean; // ✅ New field
  emailConfirmedAt?: string; // ✅ NEW: Email verification timestamp
}

interface AdminEmployerVerificationProps {
  accessToken: string;
  subAdminId?: string; // ✅ NEW: Optional sub-admin session ID
}

export function AdminEmployerVerification({ accessToken, subAdminId }: AdminEmployerVerificationProps) {
  const adminModalZ = useAdminModalZIndex();
  const [pendingEmployers, setPendingEmployers] = useState<EmployerProfile[]>([]);
  const [allEmployers, setAllEmployers] = useState<EmployerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState<'pending' | 'all'>('pending');
  const [selectedEmployer, setSelectedEmployer] = useState<EmployerProfile | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [notes, setNotes] = useState('');
  
  // Confirm dialog states
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [employerToApprove, setEmployerToApprove] = useState<string | null>(null);
  const [approvingEmployer, setApprovingEmployer] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [employerToReject, setEmployerToReject] = useState<string | null>(null);
  const [rejectingEmployer, setRejectingEmployer] = useState(false);

  useEffect(() => {
    fetchEmployers();
  }, []);

  const fetchEmployers = async () => {
    try {
      // ✅ Build headers with sub-admin session if present
      const headers: HeadersInit = {
        ...(subAdminId ? { 'X-SubAdmin-Session': subAdminId } : {}),
      };

      const [pendingRes, allRes] = await Promise.all([
        fetchWithAuth(`${API_BASE}/admin/pending-employers`, { headers }),
        fetchWithAuth(`${API_BASE}/admin/all-employers`, { headers }),
      ]);

      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        setPendingEmployers(pendingData.employers || []);
      }

      if (allRes.ok) {
        const allData = await allRes.json();
        setAllEmployers(allData.employers || []);
      }
    } catch (error) {
      devError('Error fetching employers:', error);
      toast.error('Kunne ikke laste arbeidsgivere');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = (employerId: string) => {
    setEmployerToApprove(employerId);
    setShowApproveConfirm(true);
  };

  const approveEmployer = async () => {
    if (!employerToApprove) return;
    
    setApprovingEmployer(true);
    try {
      // ✅ Build headers with sub-admin session if present
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(subAdminId ? { 'X-SubAdmin-Session': subAdminId } : {}),
      };

      const response = await fetchWithAuth(
        `${API_BASE}/admin/approve-employer`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ employer_id: employerToApprove, notes }),
        }
      );

      if (response.ok) {
        toast.success('Arbeidsgiver godkjent! E-post sendt til bruker.');
        setShowDetails(false);
        setSelectedEmployer(null);
        setNotes('');
        await fetchEmployers();
      } else {
        const error = await response.json();
        toast.error(`Feil: ${error.error || 'Kunne ikke godkjenne'}`);
      }
    } catch (error) {
      devError('Error approving employer:', error);
      toast.error('Feil ved godkjenning');
    } finally {
      setApprovingEmployer(false);
      setShowApproveConfirm(false);
      setEmployerToApprove(null);
    }
  };

  const handleRejectClick = (employerId: string) => {
    if (!notes || notes.trim().length === 0) {
      toast.error('Du må oppgi en grunn for avvisning');
      return;
    }
    setEmployerToReject(employerId);
    setShowRejectConfirm(true);
  };

  const rejectEmployer = async () => {
    if (!employerToReject) return;
    
    setRejectingEmployer(true);
    try {
      // ✅ Build headers with sub-admin session if present
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(subAdminId ? { 'X-SubAdmin-Session': subAdminId } : {}),
      };

      const response = await fetchWithAuth(
        `${API_BASE}/admin/reject-employer`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ employer_id: employerToReject, notes }),
        }
      );

      if (response.ok) {
        toast.success('Arbeidsgiver avvist. E-post sendt til bruker.');
        setShowDetails(false);
        setSelectedEmployer(null);
        setNotes('');
        await fetchEmployers();
      } else {
        const error = await response.json();
        toast.error(`Feil: ${error.error || 'Kunne ikke avvise'}`);
      }
    } catch (error) {
      devError('Error rejecting employer:', error);
      toast.error('Feil ved avvisning');
    } finally {
      setRejectingEmployer(false);
      setShowRejectConfirm(false);
      setEmployerToReject(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
            <Clock className="w-3 h-3" />
            Venter
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
            <CheckCircle className="w-3 h-3" />
            Godkjent
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
            <XCircle className="w-3 h-3" />
            Avvist
          </span>
        );
      default:
        return null;
    }
  };

  const displayedEmployers = selectedFilter === 'pending' ? pendingEmployers : allEmployers;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader size="lg" className="text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with stats and filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-yellow-100">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-5 h-5 text-yellow-600" />
            <span className="text-gray-600">Venter på godkjenning</span>
          </div>
          <p className="text-3xl text-yellow-900">{pendingEmployers.length}</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-lg border border-green-100">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-gray-600">Godkjent</span>
          </div>
          <p className="text-3xl text-green-900">
            {allEmployers.filter(e => e.verificationStatus === 'approved').length}
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-lg border border-red-100">
          <div className="flex items-center gap-3 mb-2">
            <XCircle className="w-5 h-5 text-red-600" />
            <span className="text-gray-600">Avvist</span>
          </div>
          <p className="text-3xl text-red-900">
            {allEmployers.filter(e => e.verificationStatus === 'rejected').length}
          </p>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setSelectedFilter('pending')}
          className={`px-4 py-2 rounded-full transition-all ${
            selectedFilter === 'pending'
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
              : 'bg-white text-gray-600 hover:bg-purple-50'
          }`}
        >
          Ventende ({pendingEmployers.length})
        </button>
        <button
          onClick={() => setSelectedFilter('all')}
          className={`px-4 py-2 rounded-full transition-all ${
            selectedFilter === 'all'
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
              : 'bg-white text-gray-600 hover:bg-purple-50'
          }`}
        >
          Alle ({allEmployers.length})
        </button>
      </div>

      {/* Employers table */}
      <div className="bg-white rounded-2xl shadow-lg border border-purple-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-purple-50 to-pink-50">
              <tr>
                <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Navn</th>
                <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">E-post</th>
                <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">E-post bekreftet</th>
                <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Bedrift</th>
                <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Org.nr</th>
                <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Rolle</th>
                <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Status</th>
                <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Registrert</th>
                <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Handlinger</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayedEmployers.map((employer) => (
                <tr key={employer.id} className="hover:bg-purple-50 transition-colors">
                  <td className="px-6 py-4">
                    <span className="text-gray-900">{employer.name}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-600 text-sm">{employer.email}</span>
                  </td>
                  <td className="px-6 py-4">
                    {employer.emailConfirmedAt ? (
                      <span className="inline-flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm">Ja</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600">
                        <XCircle className="w-4 h-4" />
                        <span className="text-sm">Nei</span>
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-900 font-medium">{employer.organizationNameVerified}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-600 font-mono text-sm">{employer.organizationNumber}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-700 text-sm">
                      {employer.organizationRole === 'Annet' && employer.organizationRoleOther
                        ? employer.organizationRoleOther
                        : employer.organizationRole}
                    </span>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(employer.verificationStatus)}</td>
                  <td className="px-6 py-4">
                    <span className="text-gray-600 text-sm">{formatDistanceToNow(employer.createdAt)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => {
                        setSelectedEmployer(employer);
                        setShowDetails(true);
                        setNotes(employer.verificationNotes || '');
                      }}
                      className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm flex items-center gap-1"
                    >
                      <Eye className="w-4 h-4" />
                      Detaljer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {displayedEmployers.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          {selectedFilter === 'pending' 
            ? 'Ingen arbeidsgivere venter på godkjenning' 
            : 'Ingen arbeidsgivere funnet'}
        </div>
      )}

      {/* Details Modal */}
      {showDetails && selectedEmployer && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl text-gray-900 flex items-center gap-2">
                <Briefcase className="w-6 h-6 text-purple-600" />
                Arbeidsgiver-detaljer
              </h2>
              <button
                onClick={() => {
                  setShowDetails(false);
                  setSelectedEmployer(null);
                  setNotes('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* ✅ Brønnøysund validation failed warning */}
              {selectedEmployer.bronnoyundValidationFailed && (
                <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-900 font-semibold">
                      ⚠️ Brønnøysund validering feilet!
                    </p>
                    <p className="text-red-800 text-sm mt-1">
                      Organisasjonsnummeret kunne ikke verifiseres automatisk via Brønnøysundregisteret. 
                      Dette kan skyldes at APIet var nede eller timeout. Vennligst sjekk bedriftsinformasjonen manuelt på{' '}
                      <a 
                        href={`https://www.brreg.no/bedriftssok/?q=${selectedEmployer.organizationNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-red-900 font-medium"
                      >
                        brreg.no
                      </a>{' '}
                      før du godkjenner.
                    </p>
                  </div>
                </div>
              )}

              {/* ✅ NEW: Email not confirmed warning */}
              {!selectedEmployer.emailConfirmedAt && selectedEmployer.verificationStatus === 'pending' && (
                <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-orange-900 font-semibold">
                      ⚠️ E-post ikke bekreftet!
                    </p>
                    <p className="text-orange-800 text-sm mt-1">
                      Brukeren har ikke bekreftet e-postadressen sin ennå. Selv om du godkjenner kontoen, 
                      vil de ikke kunne logge inn før e-posten er bekreftet. Du kan fortsatt godkjenne, men brukeren 
                      må først klikke på bekreftelseslinken i e-posten de mottok ved registrering.
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Navn</p>
                  <p className="text-gray-900 font-medium">{selectedEmployer.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">E-post</p>
                  <p className="text-gray-900">{selectedEmployer.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">E-post bekreftet</p>
                  {selectedEmployer.emailConfirmedAt ? (
                    <span className="inline-flex items-center gap-1 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm">Ja</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-red-600">
                      <XCircle className="w-4 h-4" />
                      <span className="text-sm">Nei</span>
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Bedriftsnavn (verifisert)</p>
                  <p className="text-gray-900 font-medium">{selectedEmployer.organizationNameVerified}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Organisasjonsnummer</p>
                  <p className="text-gray-900 font-mono">{selectedEmployer.organizationNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Rolle i bedriften</p>
                  <p className="text-gray-900">
                    {selectedEmployer.organizationRole === 'Annet' && selectedEmployer.organizationRoleOther
                      ? `${selectedEmployer.organizationRole} (${selectedEmployer.organizationRoleOther})`
                      : selectedEmployer.organizationRole}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedEmployer.verificationStatus)}</div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Registrert</p>
                  <p className="text-gray-900">{formatDistanceToNow(selectedEmployer.createdAt)}</p>
                </div>
                {selectedEmployer.verifiedAt && (
                  <div>
                    <p className="text-sm text-gray-500">Verifisert</p>
                    <p className="text-gray-900">{formatDistanceToNow(selectedEmployer.verifiedAt)}</p>
                  </div>
                )}
              </div>

              {selectedEmployer.verificationNotes && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-500 mb-1">Tidligere notat:</p>
                  <p className="text-gray-900">{selectedEmployer.verificationNotes}</p>
                </div>
              )}

              {selectedEmployer.verificationStatus === 'pending' && (
                <>
                  <div>
                    <label className="block text-sm text-gray-500 mb-2">
                      Notat (valgfritt for godkjenning, påkrevd for avvisning)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Skriv et notat om verifiseringen..."
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => handleApproveClick(selectedEmployer.id)}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-5 h-5" />
                      Godkjenn
                    </button>
                    <button
                      onClick={() => handleRejectClick(selectedEmployer.id)}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-5 h-5" />
                      Avvis
                    </button>
                  </div>
                </>
              )}

              {selectedEmployer.verificationStatus !== 'pending' && (
                <>
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-blue-900 font-medium">
                        Denne arbeidsgiveren er allerede {selectedEmployer.verificationStatus === 'approved' ? 'godkjent' : 'avvist'}.
                      </p>
                      <p className="text-blue-800 text-sm mt-1">
                        {selectedEmployer.verificationStatus === 'approved' 
                          ? 'Brukeren har tilgang til meldinger og kan kjøpe abonnement.' 
                          : 'Brukeren har ikke tilgang til meldinger eller abonnement.'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Actions for approved/rejected employers */}
                  {selectedEmployer.verificationStatus === 'approved' && (
                    <div className="flex gap-3">
                      <button
                        onClick={async () => {
                          try {
                            // ✅ Build headers with sub-admin session if present
                            const headers: HeadersInit = {
                              'Content-Type': 'application/json',
                                ...(subAdminId ? { 'X-SubAdmin-Session': subAdminId } : {}),
                            };

                              const response = await fetchWithAuth(
                              `${API_BASE}/admin/resend-approval-email`,
                              {
                                method: 'POST',
                                headers,
                              body: JSON.stringify({ employer_id: selectedEmployer.id }),
                              }
                            );
                            if (response.ok) {
                              toast.success('E-post sendt på nytt!');
                            } else {
                              const error = await response.json();
                              toast.error(`Feil: ${error.error || 'Kunne ikke sende e-post'}`);
                            }
                          } catch (error) {
                            devError('Error resending email:', error);
                            toast.error('Feil ved sending av e-post');
                          }
                        }}
                        className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                      >
                        <Mail className="w-5 h-5" />
                        Send e-post på nytt
                      </button>
                      <button
                        onClick={() => {
                          const verificationLink = `https://talentfunn.no/verify-employer?id=${selectedEmployer.id}`;
                          navigator.clipboard.writeText(verificationLink);
                          toast.success('Verifikasjonslink kopiert til utklippstavlen!');
                        }}
                        className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all flex items-center justify-center gap-2"
                      >
                        <Copy className="w-5 h-5" />
                        Kopier link
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm dialogs */}
      <ConfirmDialog
        isOpen={showApproveConfirm}
        onClose={() => {
          setShowApproveConfirm(false);
          setEmployerToApprove(null);
        }}
        onConfirm={approveEmployer}
        title="Godkjenn arbeidsgiver?"
        message="Er du sikker på at du vil godkjenne denne arbeidsgiveren?"
        confirmText="Godkjenn"
        cancelText="Avbryt"
        variant="info"
        loading={approvingEmployer}
        loadingText="Godkjenner..."
        portalZIndex={adminModalZ}
      />

      <ConfirmDialog
        isOpen={showRejectConfirm}
        onClose={() => {
          setShowRejectConfirm(false);
          setEmployerToReject(null);
        }}
        onConfirm={rejectEmployer}
        title="Avvis arbeidsgiver?"
        message="Er du sikker på at du vil avvise denne arbeidsgiveren?"
        confirmText="Avvis"
        cancelText="Avbryt"
        variant="warning"
        loading={rejectingEmployer}
        loadingText="Avviser..."
        portalZIndex={adminModalZ}
      />
    </div>
  );
}
